// Otp.tsx

import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ErrorMessage from "../../components/ErrorMessages/ErrorMessage";
// Assuming these are in the same slice file
import { setOtp, setErrorMessage, resetOtp } from "./otpSlice";

const Otp: React.FC = () => {
  const dispatch = useDispatch();
  const otp = useSelector((state: any) => state.otp.otp);
  const errorMessage = useSelector((state: any) => state.otp.errorMessage);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // FIX: Only reset OTP on mount if it's completely empty.
    // This prevents wiping out preloadedState in tests.
    if (otp.every((digit: string) => digit === '')) {
      dispatch(resetOtp());
    }
    
    // Set a small delay to ensure DOM is ready before focusing
    const timer = setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, [dispatch]); // Removed 'otp' dependency to prevent re-triggering on every change

  const handleChange = (value: string, index: number) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    if (numericValue.length > 1) return; // Prevent multiple digits in one box
    dispatch(setErrorMessage(""));

    const updatedOtp = [...otp];
    updatedOtp[index] = numericValue;
    dispatch(setOtp({ otp: updatedOtp }));

    // Move to the next box automatically
    if (numericValue && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "ArrowLeft" && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        // Set cursor position to end of the field if it has a value
        setTimeout(() => {
          if (otp[index - 1]) {
            prevInput.setSelectionRange(1, 1);
          }
        }, 0);
      }
    }

    if (e.key === "ArrowRight" && index < otp.length - 1) {
      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        nextInput.focus();
        // Set cursor position to end of the field if it has a value
        setTimeout(() => {
          if (otp[index + 1]) {
            nextInput.setSelectionRange(1, 1);
          }
        }, 0);
      }
    }

    if (e.key === "Backspace") {
      // If the input is empty, move focus to the previous input
      if (!otp[index] && index > 0) {
        const prevInput = inputRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
          // Set cursor position to end of the field if it has a value
          setTimeout(() => {
            if (otp[index - 1]) {
              prevInput.setSelectionRange(1, 1);
            }
          }, 0);
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, otp.length);
    const updatedOtp = [...otp];

    pasteData.split("").forEach((digit, i) => {
      if (i < otp.length) {
        updatedOtp[i] = digit;
      }
    });

    dispatch(setOtp({ otp: updatedOtp }));

    // Focus the last filled input box or the next empty one
    const nextIndex = pasteData.length >= otp.length ? otp.length - 1 : pasteData.length;
    inputRefs.current[nextIndex]?.focus();
  };

  // Click handler for focusing the first empty input
  const handleClickContainer = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only handle clicks on the container itself, not on input fields
    if (e.target === e.currentTarget) {
      // Find the first empty input or default to the first one
      const firstEmptyIndex = otp.findIndex((digit: string) => !digit);
      const indexToFocus = firstEmptyIndex === -1 ? 0 : firstEmptyIndex;
      
      if (inputRefs.current[indexToFocus]) {
          inputRefs.current[indexToFocus]!.focus();
      }
    }
  };

  return (
    <>
      <div 
        className={`flex flex-col items-center mt-5 ${errorMessage ? '' : 'mb-[26px]'}`}
        onClick={handleClickContainer}
        // Added for testability
        data-testid="otp-container"
      >
        <div className="flex gap-2 sm:gap-4">
          {otp.map((digit: string, index: number) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              id={`digit-${index}`}
              maxLength={1}
              autoComplete="off"
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              placeholder="*"
              className={`w-[38px] sm:w-[54px] h-12 sm:h-14 text-center text-lg border rounded-md ${
                errorMessage
                  ? "border-negative"
                  : "border-gray-300 focus:border-P_light_active"
              } focus:outline-none placeholder-gray-400 transition-colors`}
            />
          ))}
        </div>
      </div>
      {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
    </>
  );
};

export default Otp;